import { useMemo, createContext } from "react";
import { useAccountEdgeAuth } from "@pineseed/auth/hook/account";
import type { LoginProvider } from "@pineseed/auth/hook";
import { useAppInitialization } from "@pineseed/gui/hook/useAppInitialization";
import { useParticipantId } from "@pineseed/gui/hook/useParticipantId";
import { useAuthCallback } from "@pineseed/interface-gui/hook/useAuthCallback";
import { usePrivateData } from "@pineseed/interface-gui/hook/usePrivateData";
import { type ConnectionConfig } from "@pineseed/gui/hook/useConnectionStatus";
import { activeWorkspaceTagCountAtom, activeWorkspaceAtom, allActiveWorkspaceTagsAtom } from "@pineseed/workspace/state/workspaces";
import { AuthHandler, PSAuthConfig } from "@pineseed/workspace/handlers/auth-handler";
import { ParticipantId } from "@pineseed/gui/participant";

// Auth context for child components
const AuthContext = createContext<{
  isAuthenticated: boolean;
  currentUser: any;
  privateData: { apiKey?: string } | null;
  authLoading: boolean;
} | null>(null);

export { usePrivateData };

export interface UseAuthAppProps {
  pineSeedAuth: PSAuthConfig;
  httpUrl: string;
  wsUrl: string;
  accountId: string;
  apiKeyTtlS: number;
}

export interface UseAuthAppReturn {
  // Auth state
  isAuthenticated: boolean;
  currentUser: any;
  authLoading: boolean;
  privateData: { apiKey?: string } | null;
  privateDataLoading: boolean;
  privateDataError: string | null;

  // Auth functions
  loginWithPopup: () => Promise<unknown>;
  handleSignout: () => Promise<void>;
  validateSession: () => Promise<boolean>;

  // Participant state
  participantId: ParticipantId | null;
  participantName: string;
  isParticipantReady: boolean;
  isParticipantLoading: boolean;
  participantError: any;

  // Connection config
  connectionConfig: ConnectionConfig | undefined;

  // Auth functions
  authentication: {
    login: () => Promise<void>;
    logout: () => Promise<void>;
    isAuthenticated: boolean;
    authLoading: boolean;
  };

  // Auth context
  AuthContext: typeof AuthContext;
  authContextValue: {
    isAuthenticated: boolean;
    currentUser: any;
    privateData: { apiKey?: string } | null;
    authLoading: boolean;
  };

  // Atoms for ChatHeader
  activeWorkspaceTagCountAtom: typeof activeWorkspaceTagCountAtom;
  activeWorkspaceAtom: typeof activeWorkspaceAtom;
  allActiveWorkspaceTagsAtom: typeof allActiveWorkspaceTagsAtom;

  // Auth callback handling
  isAuthCallback: boolean;
  authCallbackEffect: () => void;
}

export function useAuthApp({ pineSeedAuth, httpUrl, wsUrl, accountId, apiKeyTtlS }: UseAuthAppProps): UseAuthAppReturn {
  const { participantId, isLoading: isParticipantLoading, error: participantError, isReady: isParticipantReady } = useParticipantId();

  // Login provider — same pattern as marketing's AuthContextProvider
  const loginProvider = useMemo<LoginProvider>(() => {
    const getHandler = async () => {
      const handler = AuthHandler.getInstance(pineSeedAuth);
      if (!handler.isReady()) {
        await handler.initialize({ casdoorConfig: pineSeedAuth.casdoor });
      }
      return handler;
    };
    return {
      getLoginUrl: async (state: string) => {
        const handler = await getHandler();
        return handler.getLoginUrl(state);
      },
      handleCallback: async (code: string, state: string) => {
        const handler = await getHandler();
        return handler.handleCallback(code, state);
      },
      clearStoredAuthState: async () => {
        const handler = await getHandler();
        return handler.clearStoredAuthState();
      },
    };
  }, [pineSeedAuth]);

  // Account-scoped edge auth — no workspaceId
  const {
    isAuthenticated,
    currentUser,
    loginWithPopup,
    handleSignout,
    authLoading,
    validateSession,
  } = useAccountEdgeAuth(accountId, apiKeyTtlS, httpUrl, loginProvider);

  // Initialize app with edge auth integration
  // exchangeJWTForApiKey from account auth requires a JWT arg;
  // useAppInitialization calls it with no args as a session-recovery fallback.
  // Edge auth handles exchange internally during login, so this is a no-op wrapper.
  useAppInitialization({
    authConfig: pineSeedAuth as PSAuthConfig,
    validateSession,
    exchangeJWTForApiKey: async () => true,
  });

  // Connection config — same pattern as marketing's useChatApp
  const wsUrlWithParams = useMemo(() => {
    if (!participantId?.id) return null;
    const base = wsUrl.replace(/\/ws$/, "/system/ws");
    const url = new URL(base);
    url.searchParams.set("participantId", participantId.id);
    return url.toString();
  }, [wsUrl, participantId?.id]);

  const connectionConfig = useMemo<ConnectionConfig | undefined>(() => {
    if (!wsUrlWithParams || !isAuthenticated) return undefined;
    return {
      websocket: {
        url: wsUrlWithParams,
        reconnectInterval: 3000,
        maxReconnectInterval: 60000,
        maxReconnectAttempts: 8,
      },
      http: {
        url: `${httpUrl}/health`,
        interval: 30000,
        timeout: 5000,
      },
    };
  }, [wsUrlWithParams, httpUrl, isAuthenticated]);

  // Fetch private data — auth-gated (connection health wired at App level)
  const { data: privateData, loading: privateDataLoading, error: privateDataError } = usePrivateData(
    httpUrl,
    isAuthenticated,
    isAuthenticated,
  );

  const authentication = useMemo(() => ({
    login: async () => {
      await loginWithPopup();
    },
    logout: handleSignout,
    isAuthenticated,
    authLoading,
  }), [loginWithPopup, handleSignout, isAuthenticated, authLoading]);

  // Handle authentication callback — DRY via useAuthCallback
  const { isAuthCallback, authCallbackEffect } = useAuthCallback(pineSeedAuth.casdoor.redirectPath);

  let participantName = isAuthenticated && currentUser?.name ? currentUser.name : "Guest";
  if (!isAuthenticated && currentUser?.name) participantName = `${currentUser.name} (Guest)`;

  const authContextValue = {
    isAuthenticated,
    currentUser,
    privateData,
    authLoading,
  };

  return {
    // Auth state
    isAuthenticated,
    currentUser,
    authLoading,
    privateData,
    privateDataLoading,
    privateDataError,

    // Auth functions
    loginWithPopup,
    handleSignout,
    validateSession,

    // Participant state
    participantId,
    participantName,
    isParticipantReady,
    isParticipantLoading,
    participantError,

    // Connection config
    connectionConfig,

    // Auth functions
    authentication,

    // Auth context
    AuthContext,
    authContextValue,

    // Atoms for ChatHeader
    activeWorkspaceTagCountAtom,
    activeWorkspaceAtom,
    allActiveWorkspaceTagsAtom,

    // Auth callback handling
    isAuthCallback,
    authCallbackEffect,
  };
}
